import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Link } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { CLIENT_ID_BLOOMKEEP } from '@/lib/constants';

type ClientRow = {
  id: string;
  user_id: string | null;
  name: string | null;
  email: string | null;
  created_at?: string;
};

type UserDisplay = {
  id: string;
  display_name?: string;
  email?: string;
  avatar_url?: string;
};

type AlbumRow = {
  id: string;
  slug: string;
  client_id: string | null;
};

const ClientsList: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [usersMap, setUsersMap] = useState<Map<string, UserDisplay>>(new Map());
  const [albumsByClient, setAlbumsByClient] = useState<Map<string, AlbumRow[]>>(new Map());

  // New client modal state
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newError, setNewError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        // 1) Fetch clients
        const { data: clientRows, error: clientsErr } = await supabase
          .from('clients')
          .select('id, user_id, name, email, created_at')
          .neq('id', CLIENT_ID_BLOOMKEEP) // ignores BK Client
          .order('created_at', { ascending: false });
        if (clientsErr) throw clientsErr;
        const safeClients: ClientRow[] = (clientRows as any) || [];
        setClients(safeClients);

        // 2) Fetch user display data for clients with user_id
        const userIds = safeClients.map(c => c.user_id).filter((id): id is string => !!id);
        if (userIds.length > 0) {
          const { data: userRows, error: usersErr } = await supabase.rpc('get_user_display_info', { user_ids: userIds, include_email: true });
          if (!usersErr && Array.isArray(userRows)) {
            const map = new Map<string, UserDisplay>();
            (userRows as any as UserDisplay[]).forEach(u => { if (u?.id) map.set(u.id, u); });
            setUsersMap(map);
          }
        } else {
          setUsersMap(new Map());
        }

        // 3) Fetch albums per client
        const clientIds = safeClients.map(c => c.id);
        if (clientIds.length > 0) {
          const { data: albumRows, error: albumsErr } = await supabase
            .from('albums')
            .select('id, slug, client_id')
            .in('client_id', clientIds);
          if (albumsErr) throw albumsErr;

          const byClient = new Map<string, AlbumRow[]>();
          (albumRows as any as AlbumRow[]).forEach(row => {
            if (!row.client_id) return;
            if (!byClient.has(row.client_id)) byClient.set(row.client_id, []);
            byClient.get(row.client_id)!.push(row);
          });
          // optional: sort by slug asc
          byClient.forEach(list => list.sort((a, b) => a.slug.localeCompare(b.slug)));
          setAlbumsByClient(byClient);
        } else {
          setAlbumsByClient(new Map());
        }
      } catch (e) {
        console.error('Failed to load clients', e);
        setClients([]);
        setUsersMap(new Map());
        setAlbumsByClient(new Map());
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const rows = useMemo(() => {
    return clients.map(c => {
      const usr = c.user_id ? usersMap.get(c.user_id) : undefined;
      const displayName = (usr?.display_name || '').trim() || (c.name || '').trim() || '—';
      const email = (usr?.email || '').trim() || (c.email || '').trim() || '—';
      const avatarUrl = (usr?.avatar_url || '').trim() || '';
      const albums = albumsByClient.get(c.id) || [];
      const initial = (displayName || ' ')[0]?.toUpperCase?.() || '?';
      return { id: c.id, displayName, email, avatarUrl, initial, albums };
    });
  }, [clients, usersMap, albumsByClient]);

  return (
    <div className="space-y-6">
      <div className="panel-card p-6 flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-800">Clientes</h2>
        <Button onClick={() => { setNewError(null); setNewName(''); setNewEmail(''); setNewOpen(true); }} className="rounded-xl">
          Nuevo cliente
        </Button>
      </div>

      <div className="panel-card panel-card--main p-0 overflow-hidden">
        {loading ? (
          <div className="p-6 text-slate-500">Cargando clientes…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-slate-500">No hay clientes.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[30%]">Nombre</TableHead>
                <TableHead className="w-[30%]">Email</TableHead>
                <TableHead className="w-[30%]">Eventos</TableHead>
                <TableHead className="w-[10%]">Notas</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="text-slate-800 font-medium">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={r.avatarUrl} />
                        <AvatarFallback>{r.initial}</AvatarFallback>
                      </Avatar>
                      <span>{r.displayName}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-700">{r.email}</TableCell>
                  <TableCell className="text-slate-700">
                    {r.albums.length === 0 ? (
                      <span className="text-slate-400">—</span>
                    ) : (
                      <div className="space-y-1">
                        {r.albums.map((a) => (
                          <div key={a.id}>
                            <Link to={`/panel/eventos/${a.id}`} className="text-indigo-600 hover:text-indigo-800 underline">
                              {a.slug}
                            </Link>
                          </div>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-slate-500">{/* Notas: por ahora vacío */}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* New Client Modal */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nuevo cliente</DialogTitle>
            <DialogDescription>Completá los datos para crear un nuevo cliente.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="client-name">Nombre</Label>
              <Input id="client-name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nombre del cliente" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="client-email">E-mail</Label>
              <Input id="client-email" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="usuario@mail.com" />
            </div>
            {newError && (
              <div className="text-sm text-red-600">{newError}</div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { if (!creating) { setNewOpen(false); } }}>Cancelar</Button>
            <Button
              onClick={async () => {
                if (creating) return;
                setNewError(null);
                const name = newName.trim();
                const email = newEmail.trim();
                if (!name || !email) {
                  setNewError('Completá nombre y e-mail');
                  return;
                }
                const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
                if (!emailOk) {
                  setNewError('Ingresá un e-mail válido');
                  return;
                }
                try {
                  setCreating(true);
                  const { data, error } = await supabase
                    .from('clients')
                    .insert({ name, email })
                    .select('id, user_id, name, email, created_at')
                    .single();
                  if (error || !data) {
                    throw error || new Error('No se pudo crear el cliente');
                  }
                  // Prepend new client locally
                  const newRow: ClientRow = data as any;
                  setClients((prev) => [newRow, ...prev]);
                  // No user info initially, and no albums
                  setAlbumsByClient((prev) => new Map(prev));
                  setNewOpen(false);
                  setNewName('');
                  setNewEmail('');
                  setNewError(null);
                } catch (e: any) {
                  setNewError(e?.message || 'No se pudo crear el cliente');
                } finally {
                  setCreating(false);
                }
              }}
              disabled={creating}
            >
              {creating ? (
                <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Creando…</span>
              ) : (
                'Crear'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ClientsList;
